import {useEffect} from 'react';

import {useIsNgwEnabled} from '../../util/rampFeatureFlags/rampFeatureFlags';
import {
  useResetPanelGenHistoryKeys,
  useSetPanelGenHistoryKeysResult,
} from './panelGenHistoryKeysStore';
import {sampledHistoryKeysEqual} from './sampledHistoryKeysEqual';
import {
  mapQueryResultsToStoreData,
  usePanelGenHistoryKeysQuery,
} from './usePanelGenHistoryKeysQuery';

export function usePanelGenHistoryKeysStoreSync(): void {
  const isNgwEnabled = useIsNgwEnabled();
  const {loading, error, results, resultsSearchQuery, searchErrorMessage} =
    usePanelGenHistoryKeysQuery({
      skip: !isNgwEnabled,
    });
  const setResult = useSetPanelGenHistoryKeysResult();
  const reset = useResetPanelGenHistoryKeys();

  // The panel-gen store is module-global and shared across workspaces. Reset it
  // when this workspace unmounts so old workspace's panel gen state can't leak into the next workspace.
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // Keyed on `results` so a run of the effect that was triggered by something
  // else (loading/error/search-query changing) reuses the previous object.
  // `sampledHistoryKeys` identity is a dependency of the panel-bank auto-sync
  // effect, and that effect regenerates the whole panel bank synchronously --
  // so handing it a fresh identity for an unchanged key set costs a full
  // regeneration for nothing.
  const newStoreData = mapQueryResultsToStoreData(results);

  useEffect(() => {
    if (!isNgwEnabled) {
      return;
    }
    setResult(prev => {
      // Keep prior data while Apollo is refetching for updated runset vars
      // (e.g. edit run filters, pin run, etc). Otherwise, unwanted UI flashes
      // occur during refetches.
      const next = results == null ? prev : newStoreData;
      return {
        ...next,
        // The query is no-cache, so every response (each poll tick, each
        // refetch after a run toggle) is a fresh object even when the keys are
        // the same. Pay the O(keys) comparison to retain the previous identity:
        // this avoids a key-set subscriber rerender and the auto-sync effect's
        // synchronous panel-bank regeneration. See sampledHistoryKeysEqual for
        // the measured comparison-versus-regeneration cost.
        sampledHistoryKeys: sampledHistoryKeysEqual(
          prev.sampledHistoryKeys,
          next.sampledHistoryKeys
        )
          ? prev.sampledHistoryKeys
          : next.sampledHistoryKeys,
        isLoading: loading,
        error,
        resultsSearchQuery,
        searchErrorMessage,
      };
    });
  }, [
    isNgwEnabled,
    loading,
    error,
    results,
    newStoreData,
    resultsSearchQuery,
    searchErrorMessage,
    setResult,
  ]);
}
